export type CompletionStatus = 'completed' | 'incomplete' | 'not attempted' | 'unknown';
export type SuccessStatus = 'passed' | 'failed' | 'unknown';
export type Exit = 'timeout' | 'suspend' | 'logout' | 'normal' | '';

/**
 * Common class for all SCORM API versions
 */
 abstract class Api {

	protected connection: any = null;
	protected startTime: Date;

	constructor( connection:any ) {
		this.connection = connection;
	}

	public start():boolean {

		this.initialize();
		this.score = 0;
		this.minScore = 0;
		this.maxScore = 100;

		this.startTime = new Date();
		return true;
	}

	public finish() {

		// set session time.
		let now = new Date();
		this.sessionTime = new Date( now.getTime() - this.startTime.getTime() )

		// set the exit attribute to 'suspend' in order to force the lms to store and reload the data in the next attempt
		this.exit = 'suspend';

		// terminate session
		this.terminate();
	}

	/**
	 * Begins a communication session with the LMS.
	 */
	abstract initialize():boolean;

	/**
	 * Ends a communication session with the LMS.
	 */
	abstract terminate();

	 /**
	 * Commits/Stores values set with setValue (some LMS (1.2) seem to needs this)
	 */
	abstract commit():boolean;


	/**
	 * Sets a value to the LMS.
	 * @param element Possible values see: http://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/
	 * @param value The value
	 */
	abstract setValue( element: string, value: string ): string;
	/**
	 * Retrieves a value from the LMS
	 * @param element Possible values see: http://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/
	 */

	abstract getValue( element: string ):string;
	abstract getLastError():string;
	abstract getErrorString( errorCode: string ):string;
	abstract getDiagnostic( errorCode: string ):string;


	/**
	 * Sets the score
	 * @param score Number that reflects the performance of the learner relative to the range bounded by the values of min and max
	 */
	abstract set score( score: number );

	/** 
	 * Gets the score
	 */
	abstract get score(): number;

	/**
	 * Setus the maximum score
	 * @param maxScore Maximum value in the range for the score
	 */
	abstract set maxScore( maxScore: number );

	/**
	 * Gets the maximum score
	 */
	abstract get maxScore(): number;

	/**
	 * Setus the minimum score
	 * @param minScore Minimum value in the range for the score
	 */
	abstract set minScore( minScore: number );

	/**
	 * Gets the minimum score
	 */
	abstract get minScore(): number;

	/**
	 * Set the completion status
	 * @param CompletionStatus Indicates whether the learner has completed the SCO
	 */
	abstract set completionStatus( completionStatus: CompletionStatus );

	/**
	 * Gets the completion status
	 */
	abstract get completionStatus():CompletionStatus;

	/**
	 * Sets the success status
	 * @param successStatus SuccessStatus Indicates whether the learner has mastered the SCO
	 */
	abstract set successStatus( successStatus: SuccessStatus );

	/**
	 * Gets the success status
	 */
	abstract get successStatus(): SuccessStatus;

	/**
	 * Sets the exit value
	 * @param exit Exit Indicates how or why the learner left the SCO
	 */
	abstract set exit( exit: Exit );
	/**
	 * Sets the elapsed session time
	 */
	abstract set sessionTime( now: Date );

	/**
	 * Sets the location value
	 * @param l string The learner’s current location in the SCO
	 */
	abstract set location( l: string );

	/**
	 * Gets the location value
	 */
	abstract get location(): string;

	/**
	 * Sets the suspend data value
	 * @param d string Provides space to store and retrieve data between learner sessions
	 */
	abstract set suspendData( d: string );

	/**
	 * Gets the suspend data value
	 */
	abstract get suspendData(): string;


	
}

export function searchConnection( name:string ) {
	var connection = scanFor( window, name, 10 );
	return ( connection ) ? connection : scanFor( window.opener, name, 10 );
}

function scanFor( obj:any, name:string, iteration:number = 10 ) {
	// nothing found 
	if( !obj ) return null;

	// test if we can access the obj property - cross-origin issue
	try { 
		obj[name]
	} catch( e ) {
		// if there is an exception - no success
		return null;
	}

	// test we found it
	if( obj[name] ) return obj[name];

	// test if iterations are over
	if( iteration == 0 ) return null;
	// test if there is no parent anymore
	if( obj.parent == null ) return null;

	return scanFor( obj.parent, name, --iteration );
}

export default Api;
